import { createAsyncThunk } from '@reduxjs/toolkit';
import { PaymentMethod } from '@wix/cashier-common/dist/src/enums/payments/PaymentMethod';
import { PaymentProvider } from '@wix/cashier-common/dist/src/enums/payments/PaymentProvider';
import { initApplePaySession, type InitSessionParams } from '../../services/applePay/initApplePaySession';
import { initApplePaySessionJsSdk } from '../../services/applePay/initApplePaySessionJsSdk';
import { type OnClick } from '../../types/ExternalContract';
import { inIframe } from '../../utils/inIframe';
import { onClickHandler } from '../../services/utils/onClickHandler';
import { type StoreState } from '../../types/StoreState';
import { selectAuthParams, selectTopProps } from '../../selectors';
import { type WidgetFedopsLogger } from '../../components/Provider/Fedops/FedopsProvider';
import { type BiLogger } from '../../components/Provider/BiLogger/BiLogger';
import { amountToLong } from '../../utils/amountToLong';
import { spbErrorDuringValidationFromVertical } from '../../utils/bi/events/biEvents';

export interface StartApplePayFlowParams extends InitSessionParams {
  onClick: OnClick;
  biLogger: BiLogger;
  fedopsLogger: WidgetFedopsLogger;
}

export const startApplePayFlow = createAsyncThunk<
  any,
  StartApplePayFlowParams,
  {
    state: StoreState;
  }
>('INIT_APPLE_PAY_SESSION', async (params, thunkAPI) => {
  // if (!window.ApplePaySession) {
  //   return Promise.resolve();
  // }

  // NOTE: temp solution
  // until we will be able to get site name
  // label: paymentLabel,
  // https://wix.slack.com/archives/C016CQ996P5/p1635762905162500
  // also use l18n version when Ecom unlock e-widget version on Product Page
  // params.paymentLabel = t('paymentSheet.total.label');
  params.paymentLabel = 'Store';

  const store = thunkAPI.getState();
  const auth = selectAuthParams(store);
  const topProps = selectTopProps(store);

  if (inIframe()) {
    return initApplePaySessionJsSdk(params, auth);
  }

  const session = await initApplePaySession(params, auth);

  /*
   * - Apple Pay API doesn't provide onClick callback
   * - We should create ApplePaySession on user gesture, otherwise there will be a delay before session creation and we will see an error:
   *		"InvalidAccessError: Must create a new ApplePaySession from a user gesture handler"
   * So we invoke onClick between session.begin() and user gesture.
   * For initApplePaySessionJsSdk flow this in a Thunderbolt feature-apple-pay TpaHandler
   */
  const { canceled } = await onClickHandler({
    onClick: params.onClick,
    logErrorParams: {
      message: '[ApplePay] this.props.onClick() throws exception',
      fingerprint: ['applePay', 'onClick'],
    },
  })();

  if (canceled) {
    params.biLogger.report(spbErrorDuringValidationFromVertical, {
      cartId: topProps.meta.cartId,
      productId: topProps.meta.productId,
      paymentmethod: PaymentMethod.ApplePay,
      paymentProvider: PaymentProvider.WixPayUS,
      currency: topProps.currency,
      amount: amountToLong(topProps.paymentAmount),
      // @ts-expect-error default value override
      appId: topProps.meta.appInstanceId,
    });
    return;
  }

  session.begin();
});
